import React, { useState, useEffect } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StudentDashboard = () => {
  // State declarations
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('daily');
  const [totalAdmissions, setTotalAdmissions] = useState({});
  const [admissionLeavingCertificatesCount, setAdmissionLeavingCertificatesCount] = useState(0);
  const [bonafideTakenCount, setBonafideTakenCount] = useState(0);
  const [classWiseLeavingCertificates, setClassWiseLeavingCertificates] = useState({});
  const [hasFetchedLeavingCertificates, setHasFetchedLeavingCertificates] = useState(false);

  // IndexedDB open helper
  const openIndexedDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SchoolManagementDB', 1);
      request.onerror = (event) => {
        reject(event.target.error);
      };
      request.onsuccess = (event) => {
        resolve(event.target.result);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        const stores = ['studentData', 'leavedStd', 'bonafideStd'];
        stores.forEach(store => {
          if (!db.objectStoreNames.contains(store)) {
            db.createObjectStore(store, { keyPath: store === 'leavedStd' || store === 'bonafideStd' ? 'serialNo' : 'id' });
          }
        });
      };
    });
  };

  // Fetch students data
  const fetchStudentsData = async () => {
    try {
      const db = await openIndexedDB();
      const transaction = db.transaction(['studentData'], 'readonly');
      const objectStore = transaction.objectStore('studentData');
      const allRecords = objectStore.getAll();

      allRecords.onsuccess = (event) => {
        const data = event.target.result;
        if (data) {
          setStudents(data.filter(s => s !== null));
        }
        setLoading(false);
      };
      allRecords.onerror = (event) => {
        setError('Error fetching student data from IndexedDB');
        setLoading(false);
      };
    } catch (err) {
      setError('Error fetching data');
      setLoading(false);
    }
  };

  // Fetch admission leaving certificates count
  const fetchAdmissionLeavingCertificatesCount = async () => {
    try {
      const db = await openIndexedDB();
      const transaction = db.transaction(['leavedStd'], 'readonly');
      const objectStore = transaction.objectStore('leavedStd');
      const allRecords = objectStore.getAll();

      allRecords.onsuccess = (event) => {
        const data = event.target.result || [];
        let totalCertificates = 0;
        const classCounts = {};

        data.forEach(record => {
          Object.keys(record).forEach(key => {
            if (key !== 'serialNo' && Array.isArray(record[key])) {
              totalCertificates += record[key].length;
            }
          });
        });

        students.forEach(student => {
          const studentRecord = data.find(r => r.serialNo === student.id);
          if (studentRecord && student.admissionClass) {
            let studentCertificates = 0;
            Object.keys(studentRecord).forEach(key => {
              if (key !== 'serialNo' && Array.isArray(studentRecord[key])) {
                studentCertificates += studentRecord[key].length;
              }
            });
            if (studentCertificates > 0) {
              classCounts[student.admissionClass] = (classCounts[student.admissionClass] || 0) + studentCertificates;
            }
          }
        });

        setAdmissionLeavingCertificatesCount(totalCertificates);
        setClassWiseLeavingCertificates(classCounts);
        setHasFetchedLeavingCertificates(true);
      };

      allRecords.onerror = (event) => {
        console.error('Error fetching leaving certificates:', event.target.error);
        setError('Error fetching leaving certificates');
      };
    } catch (err) {
      console.error('Error fetching leaving certificates:', err);
      setError('Error fetching leaving certificates');
    }
  };

  // Fetch bonafide certificates count
  const fetchBonafideTakenCount = async () => {
    try {
      const db = await openIndexedDB();
      const transaction = db.transaction(['bonafideStd'], 'readonly');
      const objectStore = transaction.objectStore('bonafideStd');
      const allRecords = objectStore.getAll();

      allRecords.onsuccess = (event) => {
        const data = event.target.result || [];
        setBonafideTakenCount(data.length);
      };

      allRecords.onerror = (event) => {
        console.error('Error fetching bonafide certificates:', event.target.error);
        setError('Error fetching bonafide certificates');
      };
    } catch (err) {
      console.error('Error fetching bonafide certificates:', err);
      setError('Error fetching bonafide certificates');
    }
  };

  // Initial fetches
  useEffect(() => {
    fetchStudentsData();
    fetchAdmissionLeavingCertificatesCount();
    fetchBonafideTakenCount();
  }, [fetchStudentsData, fetchAdmissionLeavingCertificatesCount, fetchBonafideTakenCount]);

  // Refresh leaving certificates when students data changes
  useEffect(() => {
    if (students.length > 0 && !hasFetchedLeavingCertificates) {
      fetchAdmissionLeavingCertificatesCount();
    }
  }, [students, hasFetchedLeavingCertificates]);

  // Calculate filtered total admissions by class based on timeframe
  const calculateTotalAdmissions = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const educationalYearStart = new Date(2024, 5, 1);
    const educationalYearEnd = new Date(2025, 4, 31);

    const admissionsCount = {};

    students.forEach(student => {
      if (!student.dateOfAdmission) return;

      const admissionDate = new Date(student.dateOfAdmission);
      const className = student.admissionClass || 'Unknown Class';

      if (!admissionsCount[className]) admissionsCount[className] = 0;

      if (timeframe === 'daily' && admissionDate.toDateString() === today.toDateString()) {
        admissionsCount[className]++;
      } else if (timeframe === 'monthly' && admissionDate.getMonth() === currentMonth && admissionDate.getFullYear() === currentYear) {
        admissionsCount[className]++;
      } else if (timeframe === 'yearly' && admissionDate >= educationalYearStart && admissionDate <= educationalYearEnd) {
        admissionsCount[className]++;
      }
    });

    setTotalAdmissions(admissionsCount);
  };

  useEffect(() => {
    if (students.length > 0) {
      calculateTotalAdmissions();
    }
  }, [students, timeframe]);

  // Process graph data for active/inactive, caste-wise, class-wise counts
  const processGraphData = () => {
    const activeStudents = students.filter(s => s.isActive !== false);
    const inactiveStudents = students.filter(s => s.isActive === false);

    const casteCounts = {};
    students.forEach(({ caste }) => {
      if (caste) casteCounts[caste] = (casteCounts[caste] || 0) + 1;
    });

    const classCounts = {};
    students.forEach(({ admissionClass }) => {
      if (admissionClass) classCounts[admissionClass] = (classCounts[admissionClass] || 0) + 1;
    });

    return {
      active: activeStudents.length,
      inactive: inactiveStudents.length,
      casteCounts,
      classCounts
    };
  };

  const graphData = processGraphData();

  // Chart data setup for active/inactive students (pie)
  const activeInactiveData = {
    labels: ['Active', 'Inactive'],
    datasets: [
      {
        label: 'Students',
        data: [graphData.active, graphData.inactive],
        backgroundColor: ['#4CAF50', '#F44336'],
        hoverOffset: 10,
      },
    ],
  };

  // Caste-wise chart data (bar)
  const casteLabels = Object.keys(graphData.casteCounts);
  const casteData = {
    labels: casteLabels,
    datasets: [
      {
        label: 'Number of Students',
        data: casteLabels.map(c => graphData.casteCounts[c]),
        backgroundColor: 'rgba(33, 150, 243, 0.7)',
      },
    ],
  };

  // Class category-wise chart data (bar)
  const classCategoryLabels = Object.keys(graphData.classCounts);
  const classCategoryData = {
    labels: classCategoryLabels,
    datasets: [
      {
        label: 'Number of Students',
        data: classCategoryLabels.map(c => graphData.classCounts[c]),
        backgroundColor: 'rgba(76, 175, 80, 0.7)',
      },
    ],
  };

  // Total admissions by class chart data (bar)
  const admissionClasses = Object.keys(totalAdmissions);
  const admissionsValues = admissionClasses.map(c => totalAdmissions[c]);

  const totalAdmissionsChartData = {
    labels: admissionClasses,
    datasets: [
      {
        label: `Total Admissions (${timeframe.charAt(0).toUpperCase() + timeframe.slice(1)})`,
        data: admissionsValues,
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
      },
    ],
  };

  // Class-wise leaving certificates chart data (bar)
  const leavingClassLabels = Object.keys(classWiseLeavingCertificates);
  const leavingClassData = {
    labels: leavingClassLabels,
    datasets: [
      {
        label: 'Number of Leaving Certificates',
        data: leavingClassLabels.map(c => classWiseLeavingCertificates[c]),
        backgroundColor: 'rgba(255, 99, 132, 0.7)',
      },
    ],
  };

  const admissionChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: true, position: 'top' },
      title: { display: true, text: 'Admissions by Class (Filtered by Timeframe)' },
    },
    scales: {
      x: { ticks: { maxRotation: 90, minRotation: 45 } },
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  const leavingChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'Leaving Certificates by Class' },
    },
    scales: {
      x: { ticks: { maxRotation: 90, minRotation: 45 } },
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  // Dropdown handler
  const handleTimeframeChange = (e) => {
    setTimeframe(e.target.value);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Student Admissions Dashboard</h2>
        <select
          value={timeframe}
          onChange={handleTimeframeChange}
          style={styles.dropdown}
          aria-label="Select time frame"
        >
          <option value="daily">Daily</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      {loading ? (
        <p style={styles.loading}>Loading student data...</p>
      ) : error ? (
        <p style={styles.error}>Error: {error}</p>
      ) : (
        <>
          {/* Total Admissions by class filtered by timeframe */}
          <div style={styles.chartBox}>
            <Bar data={totalAdmissionsChartData} options={admissionChartOptions} />
          </div>
          {/* Leaving Certificates by class */}
          <div style={styles.chartBox}>
            <h3 style={styles.chartTitle}>Leaving Certificates by Class</h3>
            {leavingClassLabels.length > 0 ? (
              <Bar data={leavingClassData} options={leavingChartOptions} />
            ) : (
              <p style={styles.noData}>No leaving certificate data available</p>
            )}
          </div>
          {/* Existing charts row */}
          <div style={styles.chartsRow}>
            <div style={styles.chartBox}>
              <h3 style={styles.chartTitle}>Active vs Inactive Students</h3>
              <Pie data={activeInactiveData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
            </div>

            <div style={styles.chartBox}>
              <h3 style={styles.chartTitle}>Caste-wise Distribution</h3>
              {casteLabels.length > 0 ? (
                <Bar
                  data={casteData}
                  options={{
                    responsive: true,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { ticks: { maxRotation: 90, minRotation: 45 } },
                      y: { beginAtZero: true, ticks: { stepSize: 1 } },
                    },
                  }}
                />
              ) : (
                <p style={styles.noData}>No caste data available</p>
              )}
            </div>

            <div style={styles.chartBox}>
              <h3 style={styles.chartTitle}>Class Category-wise Distribution</h3>
              {classCategoryLabels.length > 0 ? (
                <Bar
                  data={classCategoryData}
                  options={{
                    responsive: true,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { ticks: { maxRotation: 90, minRotation: 45 } },
                      y: { beginAtZero: true, ticks: { stepSize: 1 } },
                    },
                  }}
                />
              ) : (
                <p style={styles.noData}>No class category data available</p>
              )}
            </div>
          </div>
          {/* Display total counts */}
          <div style={styles.summary}>
            <div style={styles.summaryItem}>
              <h3>Total Leaving Certificates</h3>
              <p>{admissionLeavingCertificatesCount}</p>
            </div>
            <div style={styles.summaryItem}>
              <h3>Total Bonafide Certificates</h3>
              <p>{bonafideTakenCount}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: 1000,
    margin: '20px auto',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    padding: 20,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 30,
  },
  title: {
    fontWeight: '600',
    fontSize: 24,
    color: '#333',
  },
  dropdown: {
    padding: '6px 12px',
    fontSize: 16,
    borderRadius: 5,
    border: '1px solid #ccc',
    cursor: 'pointer',
    backgroundColor: '#fafafa',
  },
  loading: {
    textAlign: 'center',
    fontSize: 18,
    color: '#666',
  },
  error: {
    textAlign: 'center',
    fontSize: 18,
    color: 'red',
  },
  chartsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 30,
    marginTop: 30,
  },
  chartBox: {
    flex: '1 1 30%',
    minWidth: 280,
    maxHeight: 380,
    background: '#f9f9f9',
    padding: 20,
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  },
  chartTitle: {
    fontWeight: 600,
    fontSize: 18,
    marginBottom: 15,
    textAlign: 'center',
    color: '#222',
  },
  noData: {
    color: '#777',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 40,
  },
  summary: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: 40,
  },
  summaryItem: {
    backgroundColor: '#f3f3f3',
    padding: 20,
    borderRadius: 8,
    width: '45%',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
};

export default StudentDashboard;